import { Injectable, signal } from '@angular/core';
import {
  ConfirmOptions,
  ConfirmRequest,
  ToastMessage,
  ToastOptions,
  ToastVariant,
} from './notification.models';

const DEFAULT_TOAST_DURATION = 4500;

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly toastsState = signal<ToastMessage[]>([]);
  private readonly confirmState = signal<ConfirmRequest | null>(null);
  private toastCounter = 0;
  private readonly dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

  readonly toasts = this.toastsState.asReadonly();
  readonly confirmRequest = this.confirmState.asReadonly();

  success(message: string, options?: ToastOptions): string {
    return this.show('success', message, options);
  }

  error(message: string, options?: ToastOptions): string {
    return this.show('error', message, options);
  }

  warning(message: string, options?: ToastOptions): string {
    return this.show('warning', message, options);
  }

  info(message: string, options?: ToastOptions): string {
    return this.show('info', message, options);
  }

  show(variant: ToastVariant, message: string, options?: ToastOptions): string {
    const id = `toast-${++this.toastCounter}`;
    const duration = options?.duration ?? DEFAULT_TOAST_DURATION;
    const toast: ToastMessage = {
      id,
      variant,
      message,
      title: options?.title,
      duration,
      dismissible: options?.dismissible ?? true,
    };

    this.toastsState.update((current) => [...current, toast]);

    if (duration > 0) {
      const timer = setTimeout(() => this.dismiss(id), duration);
      this.dismissTimers.set(id, timer);
    }

    return id;
  }

  dismiss(id: string): void {
    const timer = this.dismissTimers.get(id);

    if (timer) {
      clearTimeout(timer);
      this.dismissTimers.delete(id);
    }

    this.toastsState.update((current) => current.filter((toast) => toast.id !== id));
  }

  dismissAll(): void {
    for (const timer of this.dismissTimers.values()) {
      clearTimeout(timer);
    }

    this.dismissTimers.clear();
    this.toastsState.set([]);
  }

  confirm(options: ConfirmOptions): Promise<boolean> {
    const pending = this.confirmState();

    if (pending) {
      pending.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      this.confirmState.set({
        options: {
          title: options.title,
          message: options.message,
          confirmText: options.confirmText ?? 'Confirm',
          cancelText: options.cancelText ?? 'Cancel',
          variant: options.variant ?? 'default',
        },
        resolve,
      });
    });
  }

  resolveConfirm(confirmed: boolean): void {
    const pending = this.confirmState();

    if (!pending) {
      return;
    }

    pending.resolve(confirmed);
    this.confirmState.set(null);
  }
}
